import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CourseType } from '../apis/api-client';
import { isAdmin as isAdminRole, isContentAuthorRole } from '../utils/roleHelpers';
import { coursesClient, enrollmentsClient } from '../apis/client-instances';
import { PageBanner, TiptapViewer } from '../components/common';
import PageHeader from '../components/common/PageHeader';
import { AddIcon, BookmarkIcon, FilterIcon, OpenInNewIcon, SchoolIcon, VisibilityIcon } from '../components/icons';
import { Box, Button, Badge, FormControlLabel, Menu, MenuItem, Switch, Typography, useMediaQuery, useTheme } from '../components/ui';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { alert, getFileUrl } from '../hooks/function';
import { useViewAsLearner } from '../utils/viewAsLearnerPref';
import { RootState } from '../types';
import FilterPanel from '../components/courses/FilterPanel';
import CourseGrid from '../components/courses/CourseGrid';
import LearnerActivitySummary from '../components/courses/LearnerActivitySummary';
import CatalogTopFilters, {
  CatalogSortBy,
  CatalogTypeValue,
} from '../components/courses/CatalogTopFilters';
import AdminCourseTable from '../components/courses/AdminCourseTable';
import AdminCourseFilters from '../components/courses/AdminCourseFilters';

export default function CoursePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const { enqueueSnackbar } = useSnackbar();
  const role = currentUser?.role;
  const { setLayoutConfig } = useLayoutConfig();

  // Admin view: admins land on the data-table view by default. The
  // "Preview as learner" affordance now lives in the sidebar (see
  // utils/viewAsLearnerPref.ts) so admins can flip into the
  // learner experience from anywhere. We subscribe to the shared
  // pref here and derive `isAdminView` from it.
  const isAdmin = isAdminRole(role);
  // See `isContentAuthorRole` for the Super-Admin-only target + why
  // `AccountAdmin` is still admitted.
  const canAuthorContent = isContentAuthorRole(role);
  const viewAsLearner = useViewAsLearner();
  const isAdminView = isAdmin && !viewAsLearner;
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);

  // Read workspace dashboard customization from account slice
  const bannerHtml = useSelector((state: RootState) => state.account.bannerHtml);
  const bannerImageUrl = useSelector((state: RootState) => state.account.bannerImageUrl);
  // `bannerImageUrl` from Redux is the SAS-storage path the admin
  // saved on the workspace — not a viewable URL. Resolve it through
  // `getFileUrl` (which is a no-op for absolute http(s) URLs, so a
  // pasted image link still works). The resolved value drives the
  // `<img>` source below.
  const [bannerImageSrc, setBannerImageSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!bannerImageUrl) {
      setBannerImageSrc(null);
      return;
    }
    (async () => {
      try {
        const resolved = await getFileUrl(bannerImageUrl);
        if (!cancelled) setBannerImageSrc(resolved || bannerImageUrl);
      } catch {
        if (!cancelled) setBannerImageSrc(bannerImageUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bannerImageUrl]);
  const dashboardWidgetHtml = useSelector((state: RootState) => state.account.dashboardWidgetHtml);

  // Check if user is authenticated
  const isAuthenticated = !!currentUser;

  // URL-based tab state
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Type facet — replaces the old All Courses / Learning Paths tabs.
  // URL still drives the default so existing links keep working:
  // `?tab=paths` lands with only "Learning Paths" selected. Empty
  // array = both types visible.
  const [typeFilter, setTypeFilter] = useState<CatalogTypeValue[]>(
    tabParam === 'paths' ? ['Learning Paths'] : []
  );

  // Internal flag that drives the API call: only when *exactly* one
  // type is selected do we narrow the server query. Empty (both) or
  // both-selected → no server-side type filter; we paint the full
  // catalog and the client-side filter (below) trims as needed.
  const onlyPaths =
    typeFilter.length === 1 && typeFilter[0] === 'Learning Paths';
  const onlyCourses = typeFilter.length === 1 && typeFilter[0] === 'Courses';

  // Tracked separately from the URL so the rest of the page (header
  // title, breadcrumbs) can still read "are we in paths view?".
  const isLearningPathsTab = onlyPaths;

  // Filter state
  const [filters, setFilters] = useState<any>({
    name: undefined,
    tagIds: undefined,
    publishedDateFrom: undefined,
    publishedDateTo: undefined,
    workspaceId: undefined,
    isDraft: undefined,
    isAssigned: undefined,
    // Default sort: A→Z by title. Applied client-side over the
    // loaded catalog (no server-side sort param available).
    sortBy: 'title' as CatalogSortBy,
  });

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Course state with deduplication
  const [displayedCourses, setDisplayedCourses] = useState<any[]>([]);
  const [courseIds, setCourseIds] = useState<Set<number>>(new Set());

  // Per-tab counts for tab labels. null = not yet loaded.
  const [counts, setCounts] = useState<{ my: number | null; all: number | null; paths: number | null }>({
    my: null,
    all: null,
    paths: null,
  });

  // Ref to track current fetch request (for race condition handling)
  const fetchIdRef = useRef(0);
  // Separate ref for count fetches so they don't race with list fetches.
  const countFetchIdRef = useRef(0);

  // Set layout configuration for this page. In admin view we hide the global
  // search since the admin table has its own scoped search — two search
  // inputs side-by-side is genuinely confusing.
  useEffect(() => {
    setLayoutConfig({
      showBackButton: false,
      // Hide the catalog-scoped topbar search for any admin on this
      // page — in admin view it duplicates the table's own search, in
      // preview-as-learner view it clutters the simulated learner
      // surface. Real learners on this page have it hidden upstream
      // via the TopBar's `isLearner` gate.
      hideGlobalSearch: isAdmin,
    });
  }, [setLayoutConfig, isAdmin]);

  // Calculate active filter count
  // Active count = filters that actually narrow results. `sortBy` is
  // a presentation choice (always set), so it doesn't count.
  const activeFilterCount = Object.entries(filters).filter(
    ([key, v]) => v !== undefined && key !== 'sortBy'
  ).length;

  // Workspace content scoping is enforced server-side: the course
  // search returns only courses the viewer's workspace owns or has been
  // provisioned (by content tag or explicit grant). The client no
  // longer filters the catalog by workspace access.

  const fetchCourses = async (resetPagination: boolean = false) => {
    // Increment fetch ID to track this request
    const fetchId = ++fetchIdRef.current;

    try {
      setLoading(true);

      const currentOffset = resetPagination ? 0 : offset;
      // No "my courses" tab anymore — that data lives in the activity
      // panel above the catalog. The catalog itself is always
      // "everything available," type-filtered when narrowed.
      const myCourses: boolean | undefined = undefined;
      // Server-side type filter only when exactly Learning Paths is
      // the sole selection; otherwise we fetch all and trim client-side.
      const courseTypeFilter = onlyPaths ? CourseType.LearningPath : undefined;

      // Tag filter sent to the backend — the admin's explicit
      // `filters.tagIds` selection, when any. Workspace content scoping
      // is applied server-side and needs no tag hint from the client.
      const apiTagIds =
        filters.tagIds && filters.tagIds.length > 0 ? filters.tagIds : undefined;

      const coursesResult = await coursesClient.getCourses(
        undefined, // id
        filters.name, // name
        undefined, // accountId
        filters.workspaceId, // workspaceId
        filters.isDraft, // isDraft
        apiTagIds, // tagIds (user filter OR workspace content-tag rules)
        filters.publishedDateFrom ? new Date(filters.publishedDateFrom) : undefined, // publishedDateFrom
        filters.publishedDateTo ? new Date(filters.publishedDateTo) : undefined, // publishedDateTo
        myCourses, // myCourses (enrolled OR created by user)
        filters.isAssigned, // isAssigned (courses with active assignments)
        courseTypeFilter, // courseType - filter for Learning Paths tab
        currentOffset, // offset
        24 // limit
      );

      // Ignore stale responses - if a newer fetch has started, discard this result
      if (fetchId !== fetchIdRef.current) {
        return;
      }

      // Check if we got the +1 result (hasMore indicator)
      const hasMoreResults = coursesResult.length === 25;
      let coursesToDisplay = hasMoreResults ? coursesResult.slice(0, 24) : coursesResult;

      // Client-side type trim — only when the user picked "Courses"
      // alone do we strip paths from the result set. (Empty type
      // filter = show everything.)
      if (onlyCourses) {
        coursesToDisplay = coursesToDisplay.filter((c: any) => c.courseType !== CourseType.LearningPath);
      }

      // Get file URLs for each course thumbnail
      const updatedCourses = await Promise.all(
        coursesToDisplay.map(async (course: any) => ({
          ...course,
          thumbnailUrl: await getFileUrl(course.thumbnail),
        }))
      );

      // Check again after async thumbnail loading
      if (fetchId !== fetchIdRef.current) {
        return;
      }

      if (resetPagination) {
        // Reset: Clear existing courses and IDs
        setDisplayedCourses(updatedCourses);
        setCourseIds(new Set(updatedCourses.map((c: any) => c.id)));
        setOffset(24);
      } else {
        // Append: Filter out duplicates
        const newCourseIds = new Set(courseIds);
        const uniqueNewCourses = updatedCourses.filter((c: any) => {
          if (newCourseIds.has(c.id)) {
            return false;
          }
          newCourseIds.add(c.id);
          return true;
        });

        setDisplayedCourses([...displayedCourses, ...uniqueNewCourses]);
        setCourseIds(newCourseIds);
        setOffset(currentOffset + 24);
      }

      setHasMore(hasMoreResults);
      setLoading(false);
    } catch (error) {
      // Only log error if this is still the current fetch
      if (fetchId === fetchIdRef.current) {
        console.error('Failed to fetch courses:', error);
        setLoading(false);
      }
    }
  };

  // Fetch per-tab counts. "My Activity" includes paths the user is enrolled in;
  // "All Courses" excludes paths (computed by subtracting pathsTotal, since
  // SearchCourseRequest can only filter FOR a type, not against it).
  const fetchCounts = async () => {
    const fetchId = ++countFetchIdRef.current;
    try {
      const baseArgs = [
        undefined, // id
        filters.name,
        undefined, // accountId
        filters.workspaceId,
        filters.isDraft,
        filters.tagIds,
        filters.publishedDateFrom ? new Date(filters.publishedDateFrom) : undefined,
        filters.publishedDateTo ? new Date(filters.publishedDateTo) : undefined,
      ] as const;

      const callCount = (myCourses: boolean | undefined, courseType: CourseType | undefined) =>
        coursesClient.getCoursesCount(
          baseArgs[0],
          baseArgs[1],
          baseArgs[2],
          baseArgs[3],
          baseArgs[4],
          baseArgs[5],
          baseArgs[6],
          baseArgs[7],
          myCourses,
          filters.isAssigned,
          courseType
        );

      if (isAuthenticated) {
        const [myTotal, allTotal, pathsTotal] = await Promise.all([
          callCount(true, undefined),
          callCount(undefined, undefined),
          callCount(undefined, CourseType.LearningPath),
        ]);
        if (fetchId !== countFetchIdRef.current) return;
        setCounts({
          my: myTotal,
          all: allTotal - pathsTotal,
          paths: pathsTotal,
        });
      } else {
        // Public/unauthenticated view: only the "Browse Courses" tab exists and
        // it shows all courses (no Learning Path exclusion on this path).
        const allTotal = await callCount(undefined, undefined);
        if (fetchId !== countFetchIdRef.current) return;
        setCounts({ my: null, all: allTotal, paths: null });
      }
    } catch (error) {
      if (fetchId === countFetchIdRef.current) {
        console.error('Failed to fetch course counts:', error);
      }
    }
  };

  // Fetch when filters or tab change (handles initial load too).
  // Skip when an admin is viewing the data-table — AdminCourseTable does its
  // own paging and refetching, so the card-grid fetch would just waste a call.
  useEffect(() => {
    if (isAdminView) return;
    // Clear previous results when the type facet flips, so paths don't
    // briefly show through while a fresh fetch is in flight.
    setDisplayedCourses([]);
    setCourseIds(new Set());
    fetchCourses(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, onlyPaths, onlyCourses, isAdminView]);

  // Counts depend only on filters + auth state, not the active tab (we fetch
  // all three so each tab label can show its count simultaneously).
  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isAuthenticated]);

  // Sort the loaded catalog client-side. Workspace content scoping is
  // already enforced server-side, so the loaded set is the visible set
  // — we only order it here. Date sorts are newest-first (most useful
  // for "what's new?"); title sort is A→Z. Missing date values sink to
  // the bottom so a half-populated field doesn't push real content
  // off-screen.
  const visibleCourses = useMemo(() => {
    const sortBy: CatalogSortBy = filters.sortBy || 'title';
    const dateKey: 'publishedDate' | 'updatedAt' | null =
      sortBy === 'publishedDate'
        ? 'publishedDate'
        : sortBy === 'lastUpdated'
          ? 'updatedAt'
          : null;
    const sorted = [...displayedCourses].sort((a: any, b: any) => {
      if (dateKey) {
        const ta = a[dateKey] ? new Date(a[dateKey]).getTime() : -Infinity;
        const tb = b[dateKey] ? new Date(b[dateKey]).getTime() : -Infinity;
        return tb - ta;
      }
      const at = (a.title || '').toString();
      const bt = (b.title || '').toString();
      return at.localeCompare(bt, undefined, { sensitivity: 'base' });
    });
    return sorted;
  }, [displayedCourses, filters.sortBy]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchCourses(false);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      name: undefined,
      tagIds: undefined,
      publishedDateFrom: undefined,
      publishedDateTo: undefined,
      workspaceId: undefined,
      isDraft: undefined,
      isAssigned: undefined,
      // Reset sort to default along with the rest.
      sortBy: 'title' as CatalogSortBy,
    });
  };

  // Type facet handler — also keeps `?tab=paths` in sync for shareable
  // links. Empty selection or both-checked clears the param.
  const handleTypeFilterChange = (next: CatalogTypeValue[]) => {
    setTypeFilter(next);
    if (next.length === 1 && next[0] === 'Learning Paths') {
      setSearchParams({ tab: 'paths' });
    } else {
      setSearchParams({});
    }
  };

  const handleCourseClick = async (course: any) => {
    // For unauthenticated users, navigate to slug-based URL
    if (!isAuthenticated) {
      navigate(`/courses/${course.slug}`);
      return;
    }

    // For external courses, navigate to detail page without auto-enrolling
    // The CourseDetail page will handle enrollment and completion separately
    if (course.courseType === CourseType.External) {
      navigate(`/courses/${course.slug}`);
      return;
    }

    const enrollment = course.currentUserEnrollment;
    const isEnrolled = enrollment !== undefined && enrollment !== null;

    try {
      if (!isEnrolled) {
        // Create enrollment for standard courses
        await enrollmentsClient.createEnrollment({
          userId: currentUser?.id,
          courseId: course.id,
        });
      }
      // Navigate to course detail page (slug-based URL for all users)
      navigate(`/courses/${course.slug}`);
    } catch (error) {
      console.error('Failed to enroll in course:', error);
      alert(enqueueSnackbar, 'Failed to enroll in course', 'error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        // Admin: pin to the available height inside <main> so only the
        // table body scrolls. Learner: minimum-height + page-flow scroll.
        ...(isAdminView ? { height: '100%', overflow: 'hidden' } : { minHeight: '100vh' }),
      }}
    >
      {/* Page Banner — learner-facing marketing surface. Hidden in admin view
          since the admin is managing content, not browsing for it. */}
      {!isAdminView && (
        bannerHtml ? (
          <TiptapViewer content={bannerHtml} />
        ) : bannerImageSrc ? (
          <Box component="img" src={bannerImageSrc} alt="Banner" sx={{ width: '100%', display: 'block' }} />
        ) : (
          <PageBanner
            title={isLearningPathsTab ? 'Learning Paths' : 'Courses'}
            subtitle={
              isLearningPathsTab
                ? 'Explore curated collections of courses to master new skills'
                : 'Discover world-class courses designed to help you achieve your goals'
            }
          />
        )
      )}

      {/* Activity summary — full-width, sits above the filter+catalog
          flex container so the filter panel below visually attaches
          only to the catalog grid (not to the activity card too). */}
      {!isAdminView && isAuthenticated && (
        <Box sx={{ px: 3, pt: 3 }}>
          <LearnerActivitySummary
            userId={
              typeof currentUser?.id === 'string'
                ? parseInt(currentUser.id)
                : (currentUser?.id as number | undefined)
            }
          />
        </Box>
      )}

      {/* Mobile filter button — learner view only. Admin view's horizontal
          toolbar already lives above the table, no drawer needed. */}
      {isMobile && !isAdminView && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => setFilterDrawerOpen(true)} fullWidth>
            <Badge badgeContent={activeFilterCount} color="primary">
              Filters
            </Badge>
          </Button>
        </Box>
      )}

      {/* Dashboard Widget — learner-only surface, sits ABOVE the
          catalog filters so admin-curated announcements / banners /
          embeds are the first thing learners see after the activity
          summary. Hidden in admin view (the working content surface
          doesn't need it eating real estate). */}
      {!isAdminView && dashboardWidgetHtml && (
        <Box sx={{ px: 3, pt: 3 }}>
          <TiptapViewer content={dashboardWidgetHtml} />
        </Box>
      )}

      {/* Top filter strip — single bar carrying everything: header,
          search, Only-Published toggle, Type facet, and the Sort-by
          selector. No more left rail; all controls cluster at the top
          of the catalog. */}
      {!isAdminView && (
        <Box
          sx={{
            px: 3,
            pt: 3,
            // Pin the filter/search/sort bar to the top of the catalog as the
            // grid scrolls, so search + sort stay reachable. Sticks against
            // <main> (MainLayout's overflow-y-auto scroller) — in learner view
            // no overflow-hidden ancestor sits between here and it.
            position: 'sticky',
            top: 0,
            zIndex: 5,
            // Solid background so grid cards scroll cleanly underneath, with a
            // soft bottom shadow to lift the pinned bar off the content.
            bgcolor: 'background.paper',
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
          }}
        >
          <CatalogTopFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            activeFilterCount={activeFilterCount}
            isAuthenticated={isAuthenticated}
            typeFilter={typeFilter}
            onTypeFilterChange={handleTypeFilterChange}
            typeCounts={{
              courses: counts.all ?? undefined,
              paths: counts.paths ?? undefined,
            }}
          />
        </Box>
      )}

      {/* Content Area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 0 }}>
        {/* Main Content — in admin view, this is the fixed-height container
            whose only scrollable descendant is the table body. In learner
            view it scrolls as a whole (the card grid is page-flow). */}
        <Box
          sx={{
            flexGrow: 1,
            ...(isAdminView
              ? { display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }
              : { overflow: 'auto' }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ...(isAdminView ? { flex: 1, minHeight: 0 } : {}),
            }}
          >

            {/* Admin action bar — uses the shared PageHeader template so
                "Manage Content" carries the same filled-tile icon, count,
                and action-slot pattern as Workspaces, Users, Tags, etc.
                The Create-popover Menu is kept outside the actions slot
                since it's anchored to a button rendered inside it. */}
            {isAdmin && (
              <Box sx={{ px: 3, pt: 3 }}>
                <PageHeader
                  icon={<SchoolIcon />}
                  title={
                    isAdminView
                      ? canAuthorContent
                        ? 'Manage Content'
                        : 'Content'
                      : isLearningPathsTab ? 'Learning Paths' : 'Courses'
                  }
                  count={
                    isAdminView && counts.all != null && counts.paths != null
                      ? counts.all + counts.paths
                      : undefined
                  }
                  description={
                    isAdminView
                      ? canAuthorContent
                        ? 'Create, edit, assign, and publish courses and learning paths for your learners.'
                        : 'Browse the catalog and assign content to your learners.'
                      : 'Previewing the learner experience.'
                  }
                  actions={
                    <>
                      {/* The "Preview as learner" toggle moved to the
                          sidebar — admins flip it from there. The page
                          header now only carries the Create CTA in
                          admin view. */}
                      {isAdminView && canAuthorContent && (
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<AddIcon />}
                          onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
                          // Branded gradient — bright cyan top-left to a
                          // slightly deeper shade bottom-right. Overrides
                          // `color="primary"` so the CTA reads as itself
                          // regardless of tenant primary color.
                          sx={{ px: 3 }}
                        >
                          Create
                        </Button>
                      )}
                    </>
                  }
                />
                {isAdminView && canAuthorContent && (
                  <>
                      <Menu
                        anchorEl={createMenuAnchor}
                        open={!!createMenuAnchor}
                        onClose={() => setCreateMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        // Custom paper — wider, softer shadow, a bit of
                        // breathing room so the menu reads as a
                        // miniature "what would you like to create?"
                        // picker rather than a tight context menu.
                        PaperProps={{
                          sx: {
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            overflow: 'hidden',
                          },
                        }}
                      >
                        {[
                          {
                            label: 'Course',
                            description: 'A standard course with lessons and quizzes',
                            icon: <SchoolIcon />,
                            color: '#1565c0',
                            href: '/courses/new',
                          },
                          {
                            label: 'Learning Path',
                            description: 'A curated sequence of courses',
                            icon: <BookmarkIcon />,
                            color: '#6a1b9a',
                            href: '/courses/new?type=path',
                          },
                          {
                            label: 'Event',
                            description: 'A scheduled live or external event',
                            icon: <OpenInNewIcon />,
                            color: '#2e7d32',
                            href: '/courses/new?type=event',
                          },
                        ].map((opt) => (
                          <MenuItem
                            key={opt.label}
                            onClick={() => {
                              setCreateMenuAnchor(null);
                              navigate(opt.href);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              py: 1.25,
                              px: 2,
                              // Pastel hover keyed to each option's accent
                              // color (matches the type-coded palette used
                              // in the curriculum builder lesson rows).
                              '&:hover': {
                                bgcolor: `${opt.color}10`,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                bgcolor: `${opt.color}18`,
                                color: opt.color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {opt.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {opt.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {opt.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                )}
              </Box>
            )}

            {/* Course view — admin table or learner card grid. In admin
                view this becomes a flex column so AdminCourseTable can
                claim the remaining height and scroll its body internally. */}
            <Box
              sx={{
                padding: 3,
                ...(isAdminView
                  ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
                  : {}),
              }}
            >
              {isAdminView ? (
                <>
                  <AdminCourseFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                  <AdminCourseTable filters={filters} />
                </>
              ) : (
                <>
                  <CourseGrid
                    courses={visibleCourses}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    onCourseClick={handleCourseClick}
                    isAuthenticated={isAuthenticated}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
